/**
 * Client-side multi-column sort utility.
 * Nulls sort to top for asc, bottom for desc.
 *
 * @param {Array} items - Array of data objects
 * @param {Array} orderSpecs - Array of { code, direction } in priority order
 * @param {function(object, string): *} [getValue] - optional value accessor
 * @returns {Array} sorted copy of items
 */
export function sortByOrder(items, orderSpecs, getValue) {
    if (!orderSpecs || orderSpecs.length === 0) return items;
    const sorted = [...items];
    sorted.sort((a, b) => {
        for (let i = 0; i < orderSpecs.length; i++) {
            const { code, direction } = orderSpecs[i];
            const valA = getValue ? getValue(a, code) : a[code];
            const valB = getValue ? getValue(b, code) : b[code];
            const emptyA = valA == null || valA === "";
            const emptyB = valB == null || valB === "";
            let cmp = 0;
            if (emptyA && emptyB) { cmp = 0; }
            else if (emptyA) { cmp = direction === "desc" ? 1 : -1; }
            else if (emptyB) { cmp = direction === "desc" ? -1 : 1; }
            else if (typeof valA === "number" && typeof valB === "number") {
                cmp = valA - valB;
                if (direction === "desc") cmp = -cmp;
            } else {
                cmp = String(valA).localeCompare(String(valB), "en", { numeric: true });
                if (direction === "desc") cmp = -cmp;
            }
            if (cmp !== 0) return cmp;
        }
        return 0;
    });
    return sorted;
}

/**
 * Parse query.order into an array of order strings.
 * Handles both array format (order=A.asc&order=B.desc) and
 * comma-separated string format (order=A.asc,B.desc).
 */
export function parseOrderArray(queryOrder) {
    if (!queryOrder) return [];
    return Array.isArray(queryOrder)
        ? queryOrder
        : String(queryOrder).split(',').map((s) => s.trim()).filter(Boolean);
}

/**
 * Build a server query that only includes the primary sort column.
 * Secondary sorts are applied client-side after data returns.
 */
export function buildServerQuery(query) {
    if (!query.order) return query;
    const orderArr = parseOrderArray(query.order);
    if (orderArr.length > 1) {
        return { ...query, order: orderArr[0] };
    }
    return query;
}

/**
 * Apply client-side sort for any number of order specs.
 * Use this when all sorting is done client-side (e.g. small fully-loaded lists).
 *
 * @param {function(object, string): *} [getValue] - optional value accessor
 */
export function applySort(items, queryOrder, getValue) {
    if (!items) return items;
    const specs = parseOrderArray(queryOrder).map((s) => {
        const [code, dir] = s.split('.');
        return { code, direction: dir || 'asc' };
    });
    return specs.length > 0 ? sortByOrder(items, specs, getValue) : items;
}

/**
 * Apply client-side multi-column sort if there are multiple order specs.
 * Filters out ARCIMEDES_ROW_ID from sort specs (tiebreaker only).
 */
export function applyClientSort(items, queryOrder) {
    if (!items) return items;
    const allOrderArr = parseOrderArray(queryOrder);
    if (allOrderArr.length <= 1) return items;
    const allSpecs = allOrderArr
        .filter((s) => s.split(".")[0] !== "ARCIMEDES_ROW_ID")
        .map((s) => {
            const [code, dir] = s.split(".");
            return { code, direction: dir || "asc" };
        });
    if (allSpecs.length > 0) {
        return sortByOrder(items, allSpecs);
    }
    return items;
}
